import { usePathname } from "next/navigation";
import { useCommandAgents } from "@/hooks/use-command-agents";
import { usePermissions } from "@/context/permissions-context";
import { isCommandPanelDisabledRoute } from "@/utils/command-panel-routes";
import type { CommandAgent } from "@/types/command-agent";

export function useActiveCommandAgent(): {
    activeAgent: CommandAgent | null;
    isLoadingActiveAgent: boolean;
    canListAgents: boolean;
} {
    const { hasAllPermissions } = usePermissions();
    const canListAgents = hasAllPermissions(["LIST_COMMAND_AGENTS"]);
    const pathname = usePathname();
    // The command panel never renders on auth/super-admin routes (see
    // isCommandPanelDisabledRoute) — there's no authenticated session to back
    // this fetch there, so skip it instead of firing 401s off a stale cached
    // permission check while the user is sitting on e.g. /login.
    const { allAgents, isLoadingAgents } = useCommandAgents({
        enabled: !isCommandPanelDisabledRoute(pathname),
    });
    // `is_active` means "shown in list", not "current agent" — prefer the
    // real is_selected signal from the activate endpoint, falling back to
    // is_active only for agents the backend hasn't tagged yet.
    const activeAgent = (allAgents.find((a) => a.is_selected) ??
        allAgents.find((a) => a.is_active) ??
        null) as CommandAgent | null;

    return {
        activeAgent,
        // Only the genuine first-load state — isFetching also flips true on every
        // silent background refetch (staleTime: 0 here), which would otherwise
        // blank the whole panel out to a loader on every refetch, not just once.
        isLoadingActiveAgent: isLoadingAgents,
        canListAgents,
    };
}
